/**
 * FLX section 02 — the flavour shelf.
 *
 * Markup ported from the client's live site (`.cat-card` / `.cat-nutri`), with
 * ONE addition the live site has no equivalent of: a price + Add to Cart row.
 * The live page does not sell yet; ours does.
 *
 * The two flavours are REAL CATALOG PRODUCTS: the admin creates and prices them
 * in the FLX admin and curates them under Storefront → Landing Packages, and this
 * shelf renders whatever is there — so the price on the page is the price at
 * checkout, and a third flavour needs no code change.
 *
 * What the catalog cannot carry is the printed foil: the nutrition table, the
 * ingredient list, the PDRI footnote and the flavour accent. Those live as
 * SKU-keyed presets in `tokens.ts` (see `FLAVOURS`) and are matched to a product
 * by SKU first, then by its position in the curated order.
 *
 * With nothing in the catalog yet the shelf renders the presets alone — full
 * labels, no price, no Add. That is exactly what the live site shows today.
 */
import { useState } from 'react';
import { peso } from '../../../../storefront/lib/format';
import type { DisplayProduct } from '../../../../storefront/lib/types';
import { ImageSlot } from '../interactive';
import { INSIDE, type FlavourPreset } from '../tokens';
import { buildShelf, useFlxShop } from './catalog';
import { isSoldOut, useAddToCart } from './use-add-to-cart';

export function FlxFlavourShelf() {
  const { flavourCards, isLoading } = useFlxShop(null);
  const entries = buildShelf(flavourCards);

  return (
    <div className="container catalog reveal">
      <div className="cat-group">
        <div className="cat-group-head">
          <span className="cat-group-no">{INSIDE.shelfKicker}</span>
          <h3>{INSIDE.shelfTitle}</h3>
          <span className="cat-group-sub">{INSIDE.shelfSub}</span>
        </div>
        <div className="cat-grid cat-grid--launch">
          {isLoading && flavourCards.length === 0
            ? [0, 1].map((i) => <div key={i} className="cat-card cat-card--skeleton" />)
            : entries.map((e, i) => (
                <FlavourCard
                  key={e.product?.id ?? e.preset?.sku ?? i}
                  product={e.product}
                  preset={e.preset}
                />
              ))}
        </div>
      </div>
    </div>
  );
}

function FlavourCard({
  product,
  preset,
}: {
  product: DisplayProduct | null;
  preset: FlavourPreset | null;
}) {
  const [factsOpen, setFactsOpen] = useState(false);
  const addToCart = useAddToCart();

  const flavour = preset?.flavour ?? product?.name ?? '';
  const sku = product?.sku ?? preset?.sku ?? '';
  const desc = preset?.tagline ?? product?.description ?? '';
  // Catalog photo wins when the admin has uploaded one; the committed pouch art
  // is the fallback so the shelf looks right before that happens.
  const image = product?.image ?? preset?.pouch ?? null;

  return (
    <article className="cat-card" style={{ ['--fx' as string]: preset?.hex ?? 'var(--orange)' }}>
      {preset && (
        <button
          type="button"
          className="cat-facts-btn"
          aria-expanded={factsOpen}
          onClick={() => setFactsOpen((v) => !v)}
        >
          Nutrition Facts
        </button>
      )}

      <div className="cat-thumb">
        <ImageSlot
          src={image}
          label={`FLX Creatine + Electrolytes ${flavour} pouch — 15 sachets`}
        />
      </div>

      <div className="cat-body">
        <div className="cat-line-tag">Creatine + Electrolytes</div>
        <div className="cat-flavor">{flavour}</div>
        <div className="cat-desc">{desc}</div>
        {sku && <div className="cat-sku">{sku}</div>}
        {product && <BuyRow product={product} onAdd={() => addToCart(product)} />}
      </div>

      {preset && (
        <div className="cat-nutri" hidden={!factsOpen}>
          <div className="cn-eyebrow">FLX Creatine + Electrolytes</div>
          <div className="cn-title">Nutrition Facts</div>
          <div className="cn-serv">{preset.serving}</div>
          <table className="cn-table">
            <tbody>
              {preset.nutrition.map((row) => (
                <tr
                  key={row.label}
                  className={
                    [row.cre && 'cn-cre', row.strong && 'cn-strong', row.indent && 'cn-sub']
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                >
                  <td>{row.label}</td>
                  <td>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="cn-ing">
            <strong>Ingredients:</strong> {preset.ingredients}
          </div>
          <div className="cn-foot">{preset.footnote}</div>
        </div>
      )}
    </article>
  );
}

/** Price + Add. Only ever rendered for a real catalog row. */
function BuyRow({ product, onAdd }: { product: DisplayProduct; onAdd: () => void }) {
  const soldOut = isSoldOut(product);
  return (
    <div className="cat-buy">
      <div className="cat-price">
        <span className="cat-price-now">{peso(product.price)}</span>
        {product.was > product.price && <span className="cat-price-was">{peso(product.was)}</span>}
      </div>
      <button
        type="button"
        className="btn btn--primary flx-add-btn"
        disabled={soldOut}
        onClick={onAdd}
      >
        {soldOut ? 'Sold Out' : 'Add to Cart'}
        {!soldOut && <span className="arrow">→</span>}
      </button>
    </div>
  );
}
